import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";

import { MassActionResults } from "@zapier/reporting/src/components/global/MassActionResults";
import { RunLog } from "@zapier/reporting/src/components/global/RunLog";
import {
  routes,
  USER_INTERACTION_EVENT,
} from "@zapier/reporting/src/constants";
import {
  useAccountAndUser,
  useEmitEvent,
  useFlags,
  useMassAction,
  useRuns,
} from "@zapier/reporting/src/hooks";
import { DateRangeLabel } from "../HistoryShared/getDateOptions";
import { HistoryLayout } from "../HistoryShared/HistoryLayout";
import { LogStreamsPromotion } from "./LogStreamsPromotion/LogStreamsPromotion";
import { StreamZapRunsButton } from "./StreamZapRunsButton";
import { useHistoryRouteFilters } from "../HistoryShared/useHistoryRouteFilters";
import { useCanAccessLogStreams } from "./useCanAccessLogStreams";
import { useZapRunModals } from "./useZapRunModals";
import { ZapRunModals } from "./ZapRunModals";

export function ZapRunsPage() {
  const router = useRouter();
  const accountAndUser = useAccountAndUser();
  const { accountId } = accountAndUser;
  const isLogStreamsEnabled =
    !accountAndUser.isLoading &&
    accountAndUser.limits.isLogStreamsEnabled === true;
  const canAccessLogStreams = useCanAccessLogStreams(
    accountId,
    isLogStreamsEnabled
  );
  const canDiscoverLogStreams =
    !accountAndUser.isLoading && isLogStreamsEnabled && canAccessLogStreams;
  const massAction = useMassAction();
  const { flags } = useFlags();
  const { emitEvent } = useEmitEvent();
  const { filters, handleFilterChange, handleFilterReset } =
    useHistoryRouteFilters({ searchTargetRoute: routes.log });
  const {
    closeErrorModal,
    closeModal,
    confirmDownloadRuns,
    modal,
    openDeleteModal,
    openDownloadModal,
    openReplayModal,
    showErrorModal,
  } = useZapRunModals(filters);

  const [lastDataFetch, setLastDataFetch] = useState(new Date());

  useEffect(() => {
    if (massAction.actionError) {
      showErrorModal(massAction.actionError);
      return;
    }

    closeErrorModal();
  }, [closeErrorModal, massAction.actionError, showErrorModal]);

  const handleOpenDeleteModal = useCallback(
    (onDelete: () => void, show: boolean) => {
      if (show) {
        openDeleteModal(onDelete);
        return;
      }

      closeModal();
    },
    [closeModal, openDeleteModal]
  );

  const handleOpenReplayModal = useCallback(
    (onReplay: () => void, show: boolean, allowTaskReplay?: boolean) => {
      if (show) {
        openReplayModal(onReplay, allowTaskReplay);
        return;
      }

      closeModal();
    },
    [closeModal, openReplayModal]
  );

  const handleRefreshData = useCallback(() => {
    setLastDataFetch(new Date());
  }, []);

  useEffect(() => {
    if (router.query.query) {
      emitEvent({
        eventName: USER_INTERACTION_EVENT,
        meta: {
          event_action: "select",
          experiment_flag: Boolean(flags?.monitor_unified_history_filter),
          experiment_name: "monitor_unified_history_filter",
          interaction_goal: "search_zap_history",
          interaction_name: "zap_history_search",
          interaction_raw_text: "Search Zap History",
          option_selected: "search",
        },
      });
    }
  }, [emitEvent, flags?.monitor_unified_history_filter, router]);

  const {
    actionableRunCount,
    error: useRunsError,
    getSelectedRuns,
    isLoading: isUseRunsLoading,
    isRefetching: isUseRunsRefetching,
    onDelete,
    onRefetch: onRunsRefetch,
    onReplay,
    runCount,
    runs,
  } = useRuns({
    accountId,
    filters,
    getAppImages: true,
    onMassAction: massAction.onMassAction,
  });

  return (
    <div>
      {massAction.message && <MassActionResults message={massAction.message} />}

      <HistoryLayout
        actions={canDiscoverLogStreams ? <StreamZapRunsButton /> : undefined}
        banner={
          canDiscoverLogStreams ? (
            <LogStreamsPromotion
              accountId={accountAndUser.accountId}
              userId={accountAndUser.userId}
            />
          ) : undefined
        }
        defaultDateKey={DateRangeLabel.Past30Days}
        filters={filters}
        onFilterChange={handleFilterChange}
        onFilterReset={handleFilterReset}
        onRefreshData={handleRefreshData}
      >
        <RunLog
          actionableRunCount={actionableRunCount}
          filters={filters}
          getSelectedRuns={getSelectedRuns}
          handleFilterChange={handleFilterChange}
          handleFilterReset={handleFilterReset}
          handleOpenDeleteModal={handleOpenDeleteModal}
          handleOpenDownloadRunsModal={openDownloadModal}
          handleOpenReplayModal={handleOpenReplayModal}
          hideStatusFilter={Boolean(flags?.monitor_unified_history_filter)}
          isUseRunsLoading={isUseRunsLoading}
          isUseRunsRefetching={isUseRunsRefetching}
          lastDataFetch={lastDataFetch}
          navigateParentWindow={router.push}
          onDelete={onDelete}
          onReplay={onReplay}
          onRunsRefetch={onRunsRefetch}
          runCount={runCount}
          runs={runs}
          showOwner={true}
          showZapDetails={true}
          useRunsError={useRunsError}
        />
      </HistoryLayout>

      <ZapRunModals
        closeModal={closeModal}
        confirmDownloadRuns={confirmDownloadRuns}
        modal={modal}
      />
    </div>
  );
}
